/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { object, array, bool } from 'prop-types';
import { useSelector } from 'react-redux';
import loadable from '@loadable/component';

import {
    getDonationsCategory, getSSRDeviceType,
} from '../../../../../../state/ducks/App/App-Selectors';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';
import ComponentErrorBoundary from '../../../EnterpriseID/Common/ComponentErrorBoundary/ComponentErrorBoundary';
import { getPresentationFamily } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const ProductRecommendationsContainer = loadable(() => import(/* webpackChunkName: "ProductRecommendationsContainer" */ '../../../ProductRecommendations/ProductRecommendationsContainer'));
const RecentlyViewedProductsCarouselContainer = loadable(() => import(/* webpackChunkName: "RecentlyViewedProductsCarouselContainer" */ '../../../EnterpriseID/RecentlyViewedProducts/RecentlyViewedProductsCarouselContainer'));

const RecommendationsBuilder = ({
    featureFlags,
    product,
    recommendations,
    sortedData,
    productBaseContent,
    isPdpRedesignEnabled,
    isPdpRedesignShortEnabled,
    isPlaRedesignEnabled,
    hideMoreRecommendation,
}) => {
    const isPlaPage = useIsPlaPage();
    const donationsCategoryName = useSelector(getDonationsCategory);
    const presentationFamily = useSelector(getPresentationFamily);
    const deviceType = useSelector(getSSRDeviceType);
    const isProductRecsEnabled = featureFlags['is-pdp-product-recommendations-enabled'] || false;
    const isRecentlyViewedProductRecsEnabled = featureFlags['is-pdp-recently-viewed-products-enabled'] || false;
    const isPLAPageRecsEnabled = featureFlags['is-recs-on-pla-enabled'] || false;
    const isPLARecentlyViewedEnabled = featureFlags['is-recs-on-recently-viewed-enabled'] || false;

    // If Category IS Donation
    const isDonationsCategory = donationsCategoryName?.toLowerCase() === 'donations';

    let showPDPRecs = isProductRecsEnabled;
    let showRecentlyViewed = isRecentlyViewedProductRecsEnabled;

    if (isPlaPage) {
        if (!isPLAPageRecsEnabled) showPDPRecs = false;
        if (!isPLARecentlyViewedEnabled) showRecentlyViewed = false;
    }

    const renderCollection = (recommendation) => {
        if (!recommendation || isDonationsCategory) {
            return null;
        }

        const recommendationKey = Object.keys(recommendation)[0];
        let recommendationData = recommendation[recommendationKey];
        const desktopRecsHeaderStyle = recommendationData?.desktop_header_style;
        const mobileRecsHeaderStyle = recommendationData?.mobile_header_style;
        recommendationData = { ...recommendationData, ...desktopRecsHeaderStyle };
        if (deviceType === 'mobile') {
            recommendationData = { ...recommendationData, ...mobileRecsHeaderStyle };
        }
        switch (recommendationKey) {
            case 'recently_viewed':
                return (
                    showRecentlyViewed && ((!isPlaRedesignEnabled && presentationFamily === 'flower') || presentationFamily === 'food') && (
                        <ComponentErrorBoundary
                            function="RecentlyViewedProductsCarouselContainer"
                            module="RecentlyViewed"
                        >
                            <RecentlyViewedProductsCarouselContainer partNumber={product.partNumber} recommendationData={recommendationData} isPdpRedesignEnabled={isPdpRedesignEnabled} isPdpRedesignShortEnabled={isPdpRedesignShortEnabled} hideMoreRecommendation={hideMoreRecommendation} isPlaRedesignEnabled={isPlaRedesignEnabled} />
                        </ComponentErrorBoundary>
                    )
                );

            case 'you_may_also_like':
                return (
                    (showPDPRecs && recommendationData.campaign_id && !hideMoreRecommendation)
                    && (
                        <ProductRecommendationsContainer
                            skuData={sortedData}
                            productCategories={product.categories}
                            partNumber={product.partNumber}
                            productName={product.name}
                            recommendationData={recommendationData}
                            isPdpRedesignEnabled={isPdpRedesignEnabled}
                            isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
                            isPlaRedesignEnabled={isPlaRedesignEnabled}
                        />
                    )
                );
            default:
                return null;
        }
    };

    const baseContentRecommendations = productBaseContent?.entries?.[0]?.recommendations?.length > 0 ? productBaseContent?.entries?.[0]?.recommendations : []; // used for styling specific product recommendations.
    // replaced product_recommendations_service with defaultContent => recommendations in StandardProductPage component for making configurable text attributes in PZ-5269.
    const productRecommendations = baseContentRecommendations.length > 0 ? baseContentRecommendations : recommendations;

    if (!productRecommendations.length) {
        return null;
    }

    return productRecommendations?.map((elem) => (
        renderCollection(elem)
    ));
};

RecommendationsBuilder.propTypes = {
    featureFlags: object.isRequired,
    product: object.isRequired,
    recommendations: array,
    sortedData: array.isRequired,
    productBaseContent: object,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
    isPlaRedesignEnabled: bool,
    hideMoreRecommendation: bool,
};

RecommendationsBuilder.defaultProps = {
    recommendations: [],
    productBaseContent: {},
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
    isPlaRedesignEnabled: false,
    hideMoreRecommendation: false,
};

export default RecommendationsBuilder;
